// Diagnostic page — proves or disproves Microsoft Search API for OneNote content.
// Accessible at /onenote-search-test (direct URL only, not in nav).
// Marc-only auth gate.

import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';

const ALLOWED = ['marc@defineholy.com', 'marc@myfiginsurance.com'];

function resolveHitUrl(webUrl) {
  if (!webUrl) return { href: '', isOneNoteScheme: false };
  const pathPart = webUrl.split('?')[0];
  const href = /\.one(\/|$)/i.test(pathPart) ? 'onenote:' + webUrl : webUrl;
  return { href, isOneNoteScheme: href.startsWith('onenote:') };
}

const ENTITY_TYPES = [
  { value: 'driveItem',           label: 'driveItem (OneDrive/SP files — primary test)' },
  { value: 'listItem',            label: 'listItem (SharePoint list items)' },
  { value: 'list',                label: 'list (SharePoint lists)' },
  { value: 'site',                label: 'site (SharePoint sites)' },
  { value: 'microsoftgraphList',  label: 'microsoftgraphList (alt)' },
];

export default function OneNoteSearchTest() {
  const { instance, accounts } = useMsal();
  const userUpn = (accounts[0]?.username || '').toLowerCase();

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  const [query, setQuery]           = useState('');
  const [entityType, setEntityType] = useState('driveItem');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [rawJson, setRawJson]       = useState(null);
  const [hits, setHits]             = useState([]);

  if (!ALLOWED.includes(userUpn)) {
    return <div style={{ padding: 24, color: '#999' }}>Not authorized.</div>;
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setRawJson(null);
    setHits([]);

    const body = {
      requests: [{
        entityTypes: [entityType],
        query: { queryString: query.trim() },
        from: 0,
        size: 25,
        fields: [
          'name', 'webUrl', 'createdDateTime',
          'lastModifiedDateTime', 'parentReference',
        ],
      }],
    };

    console.log('[OneNoteSearchTest] POST /search/query body:', JSON.stringify(body, null, 2));

    try {
      const token = await getToken();
      const res = await fetch('https://graph.microsoft.com/v1.0/search/query', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const text = await res.text();
      console.log('[OneNoteSearchTest] response status:', res.status);
      console.log('[OneNoteSearchTest] response body:', text);

      if (!res.ok) {
        setError(`HTTP ${res.status}: ${text}`);
        setRawJson(text);
        return;
      }

      const data = JSON.parse(text);
      setRawJson(JSON.stringify(data, null, 2));

      // Flatten hits from all result sets
      const allHits = [];
      for (const resultSet of (data.value || [])) {
        for (const hitsContainer of (resultSet.hitsContainers || [])) {
          for (const hit of (hitsContainer.hits || [])) {
            allHits.push(hit);
          }
        }
      }
      setHits(allHits);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 24, fontFamily: 'monospace', fontSize: 13 }}>
      <h2 style={{ fontFamily: 'sans-serif', fontSize: 18, marginBottom: 4 }}>
        Microsoft Search API — OneNote Diagnostic
      </h2>
      <p style={{ fontFamily: 'sans-serif', fontSize: 12, color: '#888', marginBottom: 20 }}>
        POST /search/query · entity type selectable · raw JSON response shown
      </p>

      <form onSubmit={handleSearch} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder='e.g. "Liège" or "Pearl sugar" or "Augustine"'
          style={{
            fontSize: 14, padding: '10px 12px', border: '1px solid #ccc',
            borderRadius: 6, fontFamily: 'inherit',
          }}
          autoFocus
        />

        <select
          value={entityType}
          onChange={e => setEntityType(e.target.value)}
          style={{
            fontSize: 13, padding: '8px 10px', border: '1px solid #ccc',
            borderRadius: 6, fontFamily: 'inherit', background: '#fff',
          }}
        >
          {ENTITY_TYPES.map(et => (
            <option key={et.value} value={et.value}>{et.label}</option>
          ))}
        </select>

        <button
          type="submit"
          disabled={loading || !query.trim()}
          style={{
            padding: '10px 20px', fontSize: 14, fontWeight: 600,
            background: loading ? '#9ab8f0' : '#1a73e8',
            color: '#fff', border: 'none', borderRadius: 6,
            cursor: loading ? 'default' : 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <div style={{ background: '#fff0f0', border: '1px solid #fbb', borderRadius: 6, padding: 12, marginBottom: 16 }}>
          <strong style={{ color: '#c0392b' }}>Error:</strong>
          <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#c0392b', fontSize: 12 }}>
            {error}
          </pre>
        </div>
      )}

      {hits.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontFamily: 'sans-serif', fontSize: 14, marginBottom: 10 }}>
            {hits.length} hit{hits.length !== 1 ? 's' : ''} — formatted
          </h3>
          {hits.map((hit, i) => {
            const r = hit.resource || {};
            const fields = r.fields || {};
            const name = r.name || fields.name || '(no name)';
            const webUrl = r.webUrl || fields.webUrl || '';
            const modified = r.lastModifiedDateTime || fields.lastModifiedDateTime || '';
            const parentRef = r.parentReference || fields.parentReference || {};
            return (
              <div
                key={i}
                style={{
                  border: '1px solid #e0e0e0', borderRadius: 6,
                  padding: '10px 14px', marginBottom: 8,
                  background: '#fafafa',
                }}
              >
                <div style={{ fontFamily: 'sans-serif', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  {name}
                </div>
                {modified && (
                  <div style={{ color: '#888', marginBottom: 4 }}>
                    Modified: {new Date(modified).toLocaleString()}
                  </div>
                )}
                {parentRef.name && (
                  <div style={{ color: '#888', marginBottom: 4 }}>
                    Parent: {parentRef.name}
                  </div>
                )}
                {webUrl && (() => {
                  const { href, isOneNoteScheme } = resolveHitUrl(webUrl);
                  return isOneNoteScheme ? (
                    <button
                      onClick={() => { window.location.href = href; }}
                      style={{
                        color: '#1a73e8', wordBreak: 'break-all', background: 'none',
                        border: 'none', cursor: 'pointer', padding: 0,
                        textAlign: 'left', textDecoration: 'underline', fontSize: 'inherit',
                      }}
                    >
                      {href}
                    </button>
                  ) : (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                       style={{ color: '#1a73e8', wordBreak: 'break-all' }}>
                      {href}
                    </a>
                  );
                })()}
                {hit.summary && (
                  <div
                    style={{ marginTop: 6, fontSize: 12, color: '#555', fontFamily: 'sans-serif' }}
                    dangerouslySetInnerHTML={{ __html: hit.summary }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {rawJson && (
        <div>
          <h3 style={{ fontFamily: 'sans-serif', fontSize: 14, marginBottom: 8 }}>Raw JSON response</h3>
          <pre style={{
            background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6,
            padding: 14, fontSize: 11, overflow: 'auto',
            maxHeight: 600, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>
            {rawJson}
          </pre>
        </div>
      )}
    </div>
  );
}
